import { defineDocument } from '../../schema';
export const blogBestPartyGamesAndTopStreamersJanuary2026Document = defineDocument({
    id: 'blog:best-party-games-and-top-streamers-january-2026',
    kind: 'blog',
    slug: 'best-party-games-and-top-streamers-january-2026',
    route: '/blog/best-party-games-and-top-streamers-january-2026',
    seo: {
        title: 'Best Party Games and Top Streamers - January 2026',
        description: 'A look at the party games and streamers that defined January 2026 on Twitch',
        imageAsset: 'twitchStats202601v3',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Best Party Games and Top Streamers - January 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'A look at the party games and streamers that defined January 2026 on Twitch',
            },
        ],
        imageAsset: 'twitchStats202601v3',
        imageAlt: 'Dynamic collage of party game elements with January 2026 badge representing the diverse party game community on Twitch',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-02-11T00:00:00.000Z'),
        tags: ['party-central'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The party game scene on Twitch is thriving. From classic favorites to emerging hits, January 2026 saw thousands of streamers bringing interactive gameplay to their communities. We tracked 28 party games played by over 20,000 unique streamers in 32 languages to see what resonated most with viewers and creators alike.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "This is the first in a monthly series tracking party game trends on Twitch. We're here to celebrate the party game community and share data about what's happening on the platform.",
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__most-streamed-party-games',
                    title: [
                        {
                            type: 'text',
                            text: 'Most Streamed Party Games',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Some party games have staying power. These five titles dominated January by sheer volume of streams captured:',
                                },
                            ],
                        },
                        {
                            type: 'list',
                            ordered: true,
                            items: [
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/among-us',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Among Us',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Social deduction with hidden imposters.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/fall-guys',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Fall Guys',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Chaotic obstacle course competition.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/golf-with-your-friends',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Golf With Your Friends',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Miniature golf with friends.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/jackbox-party-packs',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Jackbox Party Packs',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Audience participation party games.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/gartic-phone',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Gartic Phone',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Telephone meets drawing.',
                                    },
                                ],
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Methodology note',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ': Rankings based on capture count (the number of times our tracking detected live streams playing each game).',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'twitchChart202601a',
                            imageAlt: 'Streamer count by party game for January 2026',
                        },
                    ],
                },
                {
                    id: 'article__highest-average-viewership',
                    title: [
                        {
                            type: 'text',
                            text: 'Highest Average Viewership',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Capture count tells one story, but average viewer count tells another. The most-streamed games aren't always the ones pulling the biggest crowds:",
                                },
                            ],
                        },
                        {
                            type: 'list',
                            ordered: true,
                            items: [
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/the-you-quiz',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'The You Quiz',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Trivia meets personality tests.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/lycans',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Lycans',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Werewolf-style deduction gameplay.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/whatsamusic',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Whatsamusic',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Music identification challenges.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/drawy',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Drawy',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Real-time drawing competition.',
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://www.twitch.tv/directory/category/mafia-online',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Mafia Online',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Classic Mafia in digital form.',
                                    },
                                ],
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Methodology note',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ': Only includes games captured at least 10 times during January.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'twitchChart202601b',
                            imageAlt: 'Average viewer count by party game for January 2026',
                        },
                    ],
                },
                {
                    id: 'article__streamers-leading-the-party',
                    title: [
                        {
                            type: 'text',
                            text: 'Streamers Leading the Party',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'These five creators had the highest average viewer counts when captured playing party games in January:',
                                },
                            ],
                        },
                        {
                            type: 'list',
                            ordered: true,
                            items: [
                                [
                                    {
                                        type: 'link',
                                        href: 'https://twitch.tv/eliasn97',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'eliasn97',
                                            },
                                        ],
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://twitch.tv/jynxzi',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'jynxzi',
                                            },
                                        ],
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://twitch.tv/stableronaldo',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'stableronaldo',
                                            },
                                        ],
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://twitch.tv/xqc',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'xqc',
                                            },
                                        ],
                                    },
                                ],
                                [
                                    {
                                        type: 'link',
                                        href: 'https://twitch.tv/northernlion',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'northernlion',
                                            },
                                        ],
                                    },
                                ],
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Methodology note',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ': Includes streamers captured playing party games at least twice during January.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__party-games-go-global',
                    title: [
                        {
                            type: 'text',
                            text: 'Party Games Go Global',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Party games transcend language barriers. The top 10, in order of capture count: English, Spanish, German, French, Russian, Portuguese, Japanese, Italian, Polish, and Dutch.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__looking-ahead',
                    title: [
                        {
                            type: 'text',
                            text: 'Looking Ahead',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Will these trends hold? Will new games and streamers emerge to challenge the status quo? We'll find out in February's report.",
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=best-party-games-and-top-streamers-january-2026.js.map